import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { Flex, Padding } from 'components/base/layout-components';
import Text from 'components/base/Text';
import Icon from 'components/base/Icon';
import MergeTable from './MergeTable';
import styled from 'styled-components';
import GitUser from './GitUser';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import { LBButton } from '@linearb/buzz';

const Wrapper = styled(Flex)`
  width: 100%;
  height: 100%;
  flex-direction: column;
  ${({ isUnmerge }: { isUnmerge: boolean }) =>
    isUnmerge ? `justify-content: space-between;` : ''}
`;
const SelectedUsersWrapper = styled(Flex)`
  height: 100%;
  width: 100%;
  overflow: auto;
  flex-direction: column;
`;

const TableAndButtonsWrapper = styled(Flex)`
  width: 100%;
  flex-direction: column;
  overflow: hidden;
  ${({ isUnmerge }: { isUnmerge: boolean }) =>
    isUnmerge
      ? ''
      : `
  height:100%;
  justify-content:space-between;`}
`;

const prepareData = (data = [], onClick, childIds, primaryId) =>
  data
    .filter(
      ({ id, parent_id }) =>
        (!childIds.includes(id) && primaryId !== id) ||
        (parent_id && parent_id === primaryId && !childIds.includes(id))
    )
    .map(({ name, username, id, email, extended_data = {}, sensor }) => ({
      name,
      id,
      email,
      extended_data,
      sensor,
      user: (
        <GitUser
          id={id}
          name={name || username}
          email={email}
          sensor={sensor}
          onClick={onClick}
          isPrimary={undefined}
          onRemove={undefined}
        />
      )
    }));

export const VIEWS = {
  merge: 'MERGE',
  unmerge: 'UNMERGE',
  edit: 'EDIT',
  merge_disabled: 'MERGE_DISABLED'
};

const viewsToEditOptions = {
  [VIEWS.merge]: undefined,
  [VIEWS.unmerge]: { disable: true, disableSetPrimary: false },
  [VIEWS.edit]: { disable: false, disableSetPrimary: true }
};

const EditUsersModal = ({
  onSubmit,
  onCancel,
  contributors = [],
  parentId,
  view,
  connectedContributorIds = []
}) => {
  const isUnmerge = view === VIEWS.unmerge;
  const [primaryId, setPrimaryId] = useState(parentId);
  const [childIds, setChildIds] = useState(connectedContributorIds || []);

  useEffect(() => {
    setPrimaryId(parentId);
  }, [parentId]);
  useEffect(() => {
    setChildIds(connectedContributorIds || []);
  }, [connectedContributorIds]);

  const contributorsById = useMemo(
    () => _.keyBy(contributors, 'id'),
    [contributors]
  );
  const connectedContributors = _.chain(contributors)
    .filter(({ id }) => primaryId === id || childIds.includes(id))
    .map(contributor => ({
      ...contributor,
      isMainUser: parentId === contributor.id,
      isPrimary: primaryId === contributor.id
    }))
    .orderBy(['isMainUser', 'name'], ['desc', 'desc'])
    .value();

  const parent = contributorsById[parentId] || { name: '' };
  const parentName = parent.name || parent.username || '';
  const onTableContributorClick = id => {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.PEOPLE,
      page_tab: TRACKING.PAGE_TABS.EDIT_MERGE_USER,
      click_type: TRACKING.CLICK.ADDED_MERGED
    });
    setChildIds([...childIds, id]);
  };
  const data = useMemo(
    () =>
      prepareData(contributors, onTableContributorClick, childIds, primaryId),
    [contributors, childIds, primaryId]
  );

  const totalUsers = (childIds.length || 0) + 1;

  const Title = () => (
    <Flex items='center'>
      <Icon name='MergedUsers' color='osloGray' size='large' />
      <Text weight='300' size='x-x-x-large' pl='1rem'>
        {isUnmerge
          ? `${parentName} - Unmerge ${totalUsers} accounts?`
          : `${parentName} - Edit account${
              childIds.length ? `s (${totalUsers})` : ''
            }`}
      </Text>
    </Flex>
  );

  const SelectUsersText = () => (
    <Flex pt='big' pb='medium'>
      <Text size='large' weight='400'>
        {view === VIEWS.merge
          ? 'Select Git accounts to merge and set your primary account:'
          : 'Add or remove Git accounts for this member'}
      </Text>
    </Flex>
  );
  const onSelectedClick = id => {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.PEOPLE,
      page_tab: TRACKING.PAGE_TABS.EDIT_MERGE_USER,
      click_type: TRACKING.CLICK.PRIMARY_CHANGED
    });
    setChildIds([..._.without(childIds, id), primaryId]);
    setPrimaryId(id);
  };
  const onSelectedRemove = id => {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.PEOPLE,
      page_tab: TRACKING.PAGE_TABS.EDIT_MERGE_USER,
      click_type: TRACKING.CLICK.REMOVED_MERGED
    });
    setChildIds(_.without(childIds, id));
  };
  const { disableSetPrimary, disable: disableEditing } =
    viewsToEditOptions[view] || {};

  return (
    <Wrapper isUnmerge={isUnmerge}>
      <Title />
      {!isUnmerge && <SelectUsersText />}
      <Flex maxHeight={isUnmerge ? '70%' : '30%'}>
        <SelectedUsersWrapper>
          {connectedContributors.map(
            ({ id, name, username, email, isPrimary, sensor, isMainUser }) => (
              <GitUser
                isSelected
                key={id}
                id={id}
                sensor={sensor}
                email={email}
                name={name || username}
                isPrimary={isPrimary}
                onRemove={
                  disableEditing || isMainUser ? null : onSelectedRemove
                }
                onClick={
                  disableEditing || isPrimary || disableSetPrimary
                    ? null
                    : onSelectedClick
                }
              />
            )
          )}
        </SelectedUsersWrapper>
      </Flex>
      <TableAndButtonsWrapper isUnmerge={isUnmerge}>
        {!isUnmerge && (
          <MergeTable
            data={data}
            initialSearchValue={parentName.split(' ')[0]}
            prepareData={data =>
              prepareData(data, onTableContributorClick, childIds, primaryId)
            }
          />
        )}
        <Flex direction='row-reverse' pt={isUnmerge ? null : 'big'}>
          <Padding l='small'>
            <LBButton
              onClick={() => onSubmit(primaryId, childIds)}
              disabled={!isUnmerge && !childIds.length}
            >
              {isUnmerge ? 'Unmerge' : 'Save'}
            </LBButton>
          </Padding>
          <LBButton variant='outline' onClick={onCancel}>
            Cancel
          </LBButton>
        </Flex>
      </TableAndButtonsWrapper>
    </Wrapper>
  );
};

export default EditUsersModal;
